import React, { useMemo, useState } from 'react';
import { Select, Button } from 'antd';
import styles from '../../pages/projects/Projects.module.scss';
import VersionSelectOptionLabel from '../VersionSelectOptionLabel/VersionSelectOptionLabel';
import { formatVersionSelectorLabel } from '../../utils/versionDisplayName';

interface VersionManagerProps {
  fileDetail: any;
  versions: any[];
  versionsLoading: boolean;
  selectedVersion: number;
  compareMode: boolean;
  onVersionChange: (version: string) => void;
  /** Verilirse “Add version” versiyon dropdown menüsünün altında gösterilir */
  onAddVersionClick?: () => void;
  addVersionButtonLabel?: string;
  /** Select dropdown köküne eklenir (portal; örn. player üst şerit tipografi) */
  selectPopupClassName?: string;
  /** false: liste genişliği içeriğe göre (dar tetikleyici + geniş seçenek) */
  popupMatchSelectWidth?: boolean;
}

const VersionManager: React.FC<VersionManagerProps> = ({
  fileDetail,
  versions,
  versionsLoading,
  selectedVersion,
  compareMode,
  onVersionChange,
  onAddVersionClick,
  addVersionButtonLabel = '+ Add Version',
  selectPopupClassName,
  popupMatchSelectWidth,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const addInDropdown = Boolean(onAddVersionClick);
  /** Üst bileşenin ilettiği liste (anonim / Guest filtreli); yoksa geri dönüş */
  const accessibleVersions =
    versions?.length > 0 ? versions : fileDetail?.versions ?? [];

  const versionOptionClasses = useMemo(
    () => ({
      row: styles.versionSelectOptionRow,
      name: styles.versionSelectOptionName,
      date: styles.versionSelectOptionDate,
    }),
    []
  );

  const versionOptions = useMemo(() => {
    if (!accessibleVersions?.length) return [];
    return accessibleVersions.map((version: any) => ({
      label: formatVersionSelectorLabel(version.name),
      value: version.name,
      disabled: false,
    }));
  }, [accessibleVersions]);

  // Handle version selection with URL hash update
  const handleVersionSelect = (value: string) => {
    // Versiyon name'ini callback'e geç
    onVersionChange(value);
    
    // Update URL hash
    const selectedVersionData = versions.find((v: any) => v.name === value);
    if (selectedVersionData?.name) {
      window.location.hash = selectedVersionData.name;
    } else if (selectedVersionData?.path) {
      const pathParts = selectedVersionData.path.split('/');
      const versionName = pathParts[pathParts.length - 1];
      if (versionName) {
        window.location.hash = versionName;
      }
    }
  };

  // Don't render in compare mode (handled by VideoPlayerSection)
  if (compareMode) {
    return null;
  }

  return (
    <div className={styles.versionManagerContainer}>
      <div className={styles.versionSelectContent}>
        <Select
          placeholder="Select version"
          popupClassName={selectPopupClassName}
          popupMatchSelectWidth={popupMatchSelectWidth}
          value={(() => {
            if (selectedVersion && versions.length > 0) {
              const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
              return selectedVersionData?.name || selectedVersion;
            }
            return undefined;
          })()}
          onChange={handleVersionSelect}
          loading={versionsLoading}
          className={styles.versionSelect}
          options={versionOptions}
          optionRender={(opt) => {
            const value = (opt.data?.value ?? opt.value) as string;
            const v = accessibleVersions.find((x: any) => x.name === value);
            if (!v) {
              return <span>{opt.data?.label ?? opt.label}</span>;
            }
            return (
              <VersionSelectOptionLabel version={v} classes={versionOptionClasses} />
            );
          }}
          onClick={(e) => e.stopPropagation()}
          open={addInDropdown ? dropdownOpen : undefined}
          onDropdownVisibleChange={addInDropdown ? setDropdownOpen : undefined}
          dropdownRender={
            addInDropdown
              ? (menu) => (
                  <div>
                    {menu}
                    <div
                      className={styles.versionSelectDropdownFooter}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Button
                        type="primary"
                        size="small"
                        className={styles.versionSelectAddVersionBtn}
                        onClick={() => {
                          setDropdownOpen(false);
                          onAddVersionClick?.();
                        }}
                      >
                        {addVersionButtonLabel}
                      </Button>
                    </div>
                  </div>
                )
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default VersionManager; 